import { readStamped, writeStamped, safeRemoveItem, clearStampedForOwner } from './activeRoleStorage';

/**
 * The demo bar's first-visit line: has this user closed it for this workspace?
 *
 * Lives here rather than inside `DemoStageBar.vue` because it has a SECOND
 * reader — `forgetWorkspaceLocalState`, which drops it when the workspace the
 * dismissal was stamped against has been erased. A key defined in the component
 * that writes it is a key a purge quietly misses.
 *
 * Stamped per user (never the e-mail, which changes) and keyed per workspace, so
 * the next account in this browser is greeted rather than silently skipped.
 */
const INTRO_VALUE_PREFIX = 'demoStageIntroDismissed:';
const INTRO_OWNER_PREFIX = 'demoStageIntroDismissedOwner:';

function introKeys(tenantSlug: string | null | undefined): { valueKey: string; ownerKey: string } | null {
  const slug = String(tenantSlug || '').trim();
  if (!slug) return null;
  return { valueKey: `${INTRO_VALUE_PREFIX}${slug}`, ownerKey: `${INTRO_OWNER_PREFIX}${slug}` };
}

export function demoStageIntroDismissed(
  tenantSlug: string | null | undefined,
  userId: string | null | undefined,
): boolean {
  const keys = introKeys(tenantSlug);
  if (!userId || !keys) return false;
  return readStamped(keys.valueKey, keys.ownerKey, userId) === '1';
}

/**
 * Remember the dismissal. Fails open on purpose: without a workspace or an owner
 * to stamp it with there is nothing to persist, and the caller has already closed
 * the line for this tab either way.
 */
export function dismissDemoStageIntro(
  tenantSlug: string | null | undefined,
  userId: string | null | undefined,
): void {
  const keys = introKeys(tenantSlug);
  if (!userId || !keys) return;
  writeStamped(keys.valueKey, keys.ownerKey, userId, '1');
}

/** Forget it, for every account in this browser — the workspace itself is gone. */
export function clearDemoStageIntroDismissal(tenantSlug: string | null | undefined): void {
  const keys = introKeys(tenantSlug);
  if (!keys) return;
  safeRemoveItem(keys.valueKey);
  safeRemoveItem(keys.ownerKey);
}

/** Forget one account's dismissal only — a colleague's stays. */
export function clearDemoStageIntroDismissalForOwner(
  tenantSlug: string | null | undefined,
  userId: string | null | undefined,
): void {
  const keys = introKeys(tenantSlug);
  if (!keys) return;
  clearStampedForOwner(keys.valueKey, keys.ownerKey, userId);
}
